import { AuthCustomer } from '@getjust/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useCustomRouter, usePrelude } from '$hooks/client';
import { useShop } from '$hooks/queries';
import {
  useCartIdAtom,
  useEmailAtom,
  useFormMessageAtom,
  useLoginAtom,
  useTemporaryCartKeyAtom,
} from '$hooks/state';
import { CART_QUERY_KEY, USE_AUTH_LEGACY } from '$src/constants';
import { useAuth } from '$src/hooks/auth';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useShowChallengeAtom } from '$src/hooks/state/useShowChallengeAtom';
import { authAPI } from '$src/http';

const MUTATION_LOGIN_CHALLENGE_KEY = 'MUTATION/LOGIN_CHALLENGE';

export const useLoginChallenge = () => {
  const router = useCustomRouter();
  const { onEmailChange } = useEmailAtom();
  const { loginInfo, onLoginChange } = useLoginAtom();
  const { track } = useTrackAnalytics();
  const { setIsAuthenticated, setLegacyState } = useAuth();
  const { close } = useShowChallengeAtom();
  const { formMessage } = useFormMessageAtom();
  const { onCartIdChange } = useCartIdAtom();
  const { cartkey } = useTemporaryCartKeyAtom();
  const queryClient = useQueryClient();
  const { data: shop } = useShop();
  const { verifySilent, isPreludeEnabled } = usePrelude();

  // Track if silent verification is in progress
  const [isSilentVerifying, setIsSilentVerifying] = useState(false);
  const [silentVerificationFailed, setSilentVerificationFailed] = useState(false);

  // Check if silent verification should be attempted
  const shouldAttemptSilentVerification =
    isPreludeEnabled && loginInfo?.method === 'silent' && loginInfo?.requestUrl && !silentVerificationFailed;

  const mutation = useMutation({
    mutationFn: (code: string) =>
      authAPI.post<AuthCustomer.AuthChallengeResponseBody>('/challenge', {
        userId: loginInfo!.userId!,
        sessionId: loginInfo!.sessionId!,
        code,
        clientId: formMessage?.shopId ?? 'web',
        cartkey: cartkey || uuidv4(),
      }),
    mutationKey: [MUTATION_LOGIN_CHALLENGE_KEY],
    onError: async (error: any) => {
      track(AnalyticsEventName.OTP_FAILED);
      if (error.response.data.sessionId) {
        onLoginChange({ ...loginInfo, sessionId: error.response.data.sessionId });
      }
    },
    onSuccess: async ({ data }) => {
      track(AnalyticsEventName.OTP_SUCCEEDED);

      // Track if login was completed via SMS channel
      if (loginInfo?.channel === 'sms') {
        track(AnalyticsEventName.OTP_SUCCEEDED_VIA_SMS);
      }

      if (USE_AUTH_LEGACY && data.outcome === 'loggedIn') {
        setLegacyState({ accessToken: data.accessToken ?? '', refreshToken: data.refreshToken ?? '' });
      }

      if (data.outcome === 'loggedIn') {
        const preparedCartId = 'preparedCartId' in data ? data.preparedCartId : undefined;

        if (preparedCartId) {
          try {
            onCartIdChange(preparedCartId);

            // Refetch cart query immediately to load shipping methods
            // Wait for refetch to complete before navigation to ensure data is available
            if (shop?.id) {
              // Small delay to ensure cartId is propagated in all hooks before refetch
              await new Promise((resolve) => setTimeout(resolve, 100));
              await queryClient.refetchQueries({ queryKey: [CART_QUERY_KEY, preparedCartId] });
            } else {
              // If shop is not loaded, invalidate and let React Query refetch when component mounts
              queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY, preparedCartId] });
            }
          } catch (error) {
            console.warn('Failed to update cartId with preparedCartId:', error);
          }
        } else {
          console.warn('preparedCartId is not present in the response', {
            data,
            cartkey,
            hook: useLoginChallenge.name,
          });
        }
      }

      setIsAuthenticated(true);
      onEmailChange();
      close();
      // Hold the navigation until the OTP card's AnimatePresence exit
      // animation has played (450 ms duration in MobileFieldsLayout's
      // OTP slot) plus a small buffer. Without this wait, `router.push`
      // unmounts the unauthenticated page mid-animation — the OTP card
      // never gets to visibly close and the transition reads as a
      // jarring page reload. With the wait, the user sees: OTP slides
      // out → seamless route swap to /authenticated → already-filled
      // email row. One continuous motion instead of a flash.
      await new Promise((resolve) => setTimeout(resolve, 500));
      router.push('/authenticated');
    },
  });

  // Attempt silent verification when conditions are met
  const attemptSilentVerification = useCallback(async () => {
    if (!shouldAttemptSilentVerification || !loginInfo?.requestUrl) {
      return;
    }

    setIsSilentVerifying(true);

    try {
      const verificationCode = await verifySilent(loginInfo.requestUrl);
      // Submit the code obtained from silent verification
      await mutation.mutateAsync(verificationCode);
    } catch (error) {
      console.warn('Silent verification failed, falling back to manual OTP entry', error);
      setSilentVerificationFailed(true);
      // User will need to enter the OTP code manually
    } finally {
      setIsSilentVerifying(false);
    }
  }, [shouldAttemptSilentVerification, loginInfo?.requestUrl, verifySilent, mutation]);

  // Trigger silent verification when loginInfo changes and conditions are met
  useEffect(() => {
    if (shouldAttemptSilentVerification && !isSilentVerifying) {
      attemptSilentVerification();
    }
  }, [shouldAttemptSilentVerification, isSilentVerifying, attemptSilentVerification]);

  return {
    onChallenge: mutation.mutateAsync,
    status: mutation.status,
    // Expose silent verification state for UI handling
    isSilentVerifying,
    silentVerificationFailed,
    shouldShowOtpInput: !shouldAttemptSilentVerification || silentVerificationFailed,
  };
};
